import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  ShieldCheck, ScrollText, Activity, BookOpen, Download as DownloadIcon,
  History as HistoryIcon, Plug, LifeBuoy, Search, Loader2, FileText,
  AlertCircle,
} from "lucide-react";
import { partnerApi, formatPartnerError } from "./partnerApi";
import "./PartnerPortal.css";

const SECTIONS = [
  { key: "security",         label: "Security",         icon: ShieldCheck },
  { key: "compliance",       label: "Compliance",       icon: ScrollText },
  { key: "operations",       label: "Operations",       icon: Activity },
  { key: "documentation",    label: "Documentation",    icon: BookOpen },
  { key: "downloads",        label: "Downloads",        icon: DownloadIcon },
  { key: "release-notes",    label: "Release Notes",    icon: HistoryIcon },
  { key: "api-integrations", label: "API & Integrations", icon: Plug },
  { key: "support",          label: "Support",          icon: LifeBuoy },
];

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

export default function PartnerTrustCenter() {
  const [params, setParams] = useSearchParams();
  const [section, setSection] = useState(params.get("section") || "security");
  const [overview, setOverview] = useState(null);
  const [err, setErr] = useState("");
  const [globalQ, setGlobalQ] = useState("");
  const [globalResults, setGlobalResults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await partnerApi.get("/partner/portal/trust-center/overview");
        setOverview(data);
      } catch (e) { setErr(formatPartnerError(e)); }
    })();
  }, []);

  const onChangeSection = (key) => {
    setSection(key);
    setParams({ section: key });
  };

  const runGlobalSearch = async (q) => {
    setGlobalQ(q);
    if (!q || q.length < 2) { setGlobalResults(null); return; }
    try {
      const { data } = await partnerApi.get("/partner/portal/trust-center/search",
        { params: { q } });
      setGlobalResults(data);
    } catch (e) { /* ignore transient */ }
  };

  return (
    <div className="hw-pp__page" data-testid="partner-trust-center-page">
      <header className="hw-pp__page-header">
        <div>
          <div className="hw-pp__eyebrow">HarborWell Connect · Partner Portal</div>
          <h1 className="hw-pp__page-title">Trust Center</h1>
          <p className="hw-pp__page-sub">
            Security, compliance, operational, and integration documentation
            for approved partner organizations.
            {overview?.partner_org?.name && (
              <> Signed in as <strong>{overview.partner_org.name}</strong>.</>
            )}
          </p>
        </div>
        <div style={{ minWidth: 280 }}>
          <div style={{ position: "relative" }}>
            <Search className="w-4 h-4"
                     style={{ position: "absolute", top: 10, left: 10,
                                color: "#64748B" }} />
            <input type="search"
                    placeholder="Search pages, documents, releases…"
                    value={globalQ}
                    onChange={(e) => runGlobalSearch(e.target.value)}
                    data-testid="tc-global-search"
                    className="hw-pp__input"
                    style={{ paddingLeft: 32 }} />
          </div>
          {globalResults && (
            <div className="hw-pp__card"
                  style={{ marginTop: 8, padding: 12, maxHeight: 380, overflow: "auto" }}
                  data-testid="tc-search-results">
              <div className="hw-pp__caption">Results for "{globalResults.query}"</div>
              <SearchGroup label="Pages" items={globalResults.pages}
                              onPick={(p) => { onChangeSection(p.section);
                                                setGlobalResults(null);
                                                setGlobalQ(""); }} />
              <SearchGroup label="Documents" items={globalResults.documents}
                              onPick={() => { onChangeSection("downloads");
                                                setGlobalResults(null); }} />
              <SearchGroup label="Releases" items={globalResults.releases}
                              onPick={() => { onChangeSection("release-notes");
                                                setGlobalResults(null); }} />
            </div>
          )}
        </div>
      </header>

      {err && (
        <div className="hw-pp__error" data-testid="tc-error"
              style={{ maxWidth: "100%" }}>{err}</div>
      )}

      <div className="hw-pp__tabbar" data-testid="tc-tabbar">
        {SECTIONS.map(({ key, label, icon: Icon }) => {
          const count = overview?.counts_by_section?.[key];
          const active = section === key;
          return (
            <button key={key}
                     onClick={() => onChangeSection(key)}
                     data-testid={`tc-tab-${key}`}
                     className={`hw-pp__tab ${active ? "hw-pp__tab--active" : ""}`}>
              <Icon className="w-4 h-4" />
              <span>{label}</span>
              {count != null && count > 0 && (
                <span className="hw-pp__tab-count">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="hw-pp__card" style={{ padding: 20, marginTop: 12 }}>
        {section === "operations"     ? <OperationsPanel />
         : section === "downloads"     ? <DownloadsPanel />
         : section === "release-notes" ? <ReleasesPanel />
                                        : <PagesPanel section={section} />}
      </div>
    </div>
  );
}

function SearchGroup({ label, items, onPick }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div className="hw-pp__caption" style={{ marginBottom: 4 }}>{label}</div>
      <ul style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map((it) => (
          <li key={it.id}>
            <button onClick={() => onPick(it)}
                     className="hw-pp__link"
                     data-testid={`tc-search-item-${label.toLowerCase()}-${it.id.slice(0, 6)}`}
                     style={{ textAlign: "left", width: "100%",
                               padding: "6px 8px", borderRadius: 6 }}>
              <div style={{ fontWeight: 500 }}>{it.title || it.version || it.filename}</div>
              {it.summary && (
                <div style={{ fontSize: 11, color: "#94A3B8" }}>{it.summary.slice(0, 100)}</div>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Pages panel (Security / Compliance / Documentation / API / Support) ─────
function PagesPanel({ section }) {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setList(null);
    try {
      const { data } = await partnerApi.get(
        "/partner/portal/trust-center/pages",
        { params: { section, search: q || undefined, page_size: 100 } },
      );
      setList(data);
      setSelected(data.items[0] || null);
    } catch (e) {
      setList({ total: 0, items: [] });
    }
  }, [section, q]);

  useEffect(() => { load(); }, [load]);

  if (!list) return <LoadingRow />;
  if (!list.items.length) {
    return <EmptyState label="No content published in this section yet." />;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 20 }}
          data-testid={`tc-panel-${section}`}>
      <aside>
        <input type="search" placeholder="Filter pages…"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="hw-pp__input"
                data-testid="tc-page-filter" />
        <ul style={{ marginTop: 12, display: "flex",
                       flexDirection: "column", gap: 4 }}>
          {list.items.map((p) => (
            <li key={p.id}>
              <button onClick={() => setSelected(p)}
                       data-testid={`tc-page-item-${p.slug}`}
                       className={`hw-pp__side-item ${selected?.id === p.id ? "hw-pp__side-item--active" : ""}`}>
                <FileText className="w-3.5 h-3.5" />
                <span>{p.title}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <article data-testid="tc-page-body">
        {selected ? (
          <>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: "#F8FAFC", marginBottom: 4 }}
                  data-testid="tc-page-title">{selected.title}</h2>
            {selected.summary && (
              <p style={{ color: "#94A3B8", marginBottom: 12 }}>{selected.summary}</p>
            )}
            <div className="hw-pp__prose" data-testid="tc-page-md">
              <ReactMarkdown>{selected.body_md || ""}</ReactMarkdown>
            </div>
            <div className="hw-pp__caption" style={{ marginTop: 24 }}>
              Version {selected.version} · Updated {fmtDate(selected.updated_at)}
            </div>
          </>
        ) : <EmptyState label="Select a page from the list." />}
      </article>
    </div>
  );
}

// ─── Operations panel — reuses public status feeds ──────────────────────────
function OperationsPanel() {
  const [ops, setOps] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => { (async () => {
    try {
      const [o, s] = await Promise.all([
        partnerApi.get("/partner/portal/trust-center/operations"),
        partnerApi.get("/public/trust-center/status"),
      ]);
      setOps(o.data);
      setStatus(s.data);
    } catch { /* ignore */ }
  })(); }, []);

  if (!ops) return <LoadingRow />;
  return (
    <div data-testid="tc-panel-operations">
      <h2 style={{ fontSize: 20, fontWeight: 600, color: "#F8FAFC" }}>Platform status</h2>
      <p style={{ color: "#94A3B8", marginBottom: 12 }}>
        Live operational health, incident history, and scheduled maintenance
        for the HarborWell Connect platform.
      </p>
      {status && (
        <div className="hw-pp__stat-grid" style={{ marginBottom: 20 }}>
          <StatCard label="Overall status" value={status.overall_status || "operational"} />
          <StatCard label="Uptime (30d)" value={status.uptime_30d ? `${status.uptime_30d}%` : "—"} />
          <StatCard label="Active incidents"
                       value={ops.incidents.filter((i) => !i.resolved_at).length} />
          <StatCard label="Scheduled maintenance"
                       value={ops.maintenance.length} />
        </div>
      )}
      <Section title="Recent incidents">
        {ops.incidents.length === 0
          ? <EmptyState label="No incidents recorded." />
          : <ul className="hw-pp__list" data-testid="tc-ops-incidents">
              {ops.incidents.map((i) => (
                <li key={i.id} className="hw-pp__row">
                  <div>
                    <strong>{i.title}</strong>
                    <div style={{ fontSize: 12, color: "#94A3B8" }}>
                      {fmtDate(i.started_at)} · {i.status || "resolved"}
                    </div>
                  </div>
                  {i.summary && <span className="hw-pp__muted">{i.summary}</span>}
                </li>
              ))}
            </ul>}
      </Section>
      <Section title="Scheduled maintenance">
        {ops.maintenance.length === 0
          ? <EmptyState label="No maintenance scheduled." />
          : <ul className="hw-pp__list" data-testid="tc-ops-maintenance">
              {ops.maintenance.map((m) => (
                <li key={m.id} className="hw-pp__row">
                  <div>
                    <strong>{m.title}</strong>
                    <div style={{ fontSize: 12, color: "#94A3B8" }}>
                      {fmtDate(m.starts_at)} — {fmtDate(m.ends_at)}
                    </div>
                  </div>
                  {m.summary && <span className="hw-pp__muted">{m.summary}</span>}
                </li>
              ))}
            </ul>}
      </Section>
      <Section title="Announcements">
        {ops.announcements.length === 0
          ? <EmptyState label="No announcements." />
          : <ul className="hw-pp__list" data-testid="tc-ops-announcements">
              {ops.announcements.map((a) => (
                <li key={a.id} className="hw-pp__row">
                  <div>
                    <strong>{a.title}</strong>
                    <div style={{ fontSize: 12, color: "#94A3B8" }}>
                      {fmtDate(a.published_at)}
                    </div>
                  </div>
                  {a.body && <span className="hw-pp__muted">{a.body.slice(0, 200)}</span>}
                </li>
              ))}
            </ul>}
      </Section>
    </div>
  );
}

// ─── Downloads panel ────────────────────────────────────────────────────────
function DownloadsPanel() {
  const [q, setQ] = useState("");
  const [section, setSectionFilter] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data } = await partnerApi.get(
        "/partner/portal/trust-center/documents",
        { params: { search: q || undefined,
                     section: section || undefined,
                     page, page_size: 25 } },
      );
      setData(data);
    } catch (e) { setErr(formatPartnerError(e)); }
  }, [q, section, page]);

  useEffect(() => { load(); }, [load]);

  const download = async (doc) => {
    setDownloading(doc.id);
    try {
      const resp = await partnerApi.get(
        `/partner/portal/trust-center/documents/${doc.id}/download`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: doc.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatPartnerError(e)); }
    finally { setDownloading(null); }
  };

  return (
    <div data-testid="tc-panel-downloads">
      <h2 style={{ fontSize: 20, fontWeight: 600, color: "#F8FAFC" }}>Document library</h2>
      <p style={{ color: "#94A3B8", marginBottom: 12 }}>
        Whitepapers, agreements, integration guides, and other partner documents.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
                placeholder="Search documents…" className="hw-pp__input"
                data-testid="tc-doc-search" style={{ flex: 1 }} />
        <select value={section}
                 onChange={(e) => { setPage(1); setSectionFilter(e.target.value); }}
                 data-testid="tc-doc-section-filter"
                 className="hw-pp__input" style={{ maxWidth: 240 }}>
          <option value="">All sections</option>
          {SECTIONS.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
        </select>
      </div>
      {err && <div className="hw-pp__error">{err}</div>}
      {!data ? <LoadingRow /> :
        data.items.length === 0
          ? <EmptyState label="No documents match your filters." />
          : (
            <>
              <table className="hw-pp__table" data-testid="tc-doc-table">
                <thead>
                  <tr>
                    <th>Title</th><th>Section</th><th>Version</th>
                    <th>Size</th><th>Uploaded</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((d) => (
                    <tr key={d.id} data-testid={`tc-doc-row-${d.id.slice(0, 8)}`}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{d.title}</div>
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>{d.filename}</div>
                        {d.summary && <div style={{ fontSize: 11, color: "#94A3B8" }}>{d.summary}</div>}
                      </td>
                      <td>{d.section}</td>
                      <td>v{d.version}</td>
                      <td>{fmtSize(d.size)}</td>
                      <td>{fmtDate(d.created_at)}</td>
                      <td>
                        <button onClick={() => download(d)}
                          disabled={downloading === d.id}
                          data-testid={`tc-doc-download-${d.id.slice(0, 8)}`}
                          className="hw-pp__btn hw-pp__btn--primary hw-pp__btn--sm">
                          {downloading === d.id ? <Loader2 className="w-3 h-3 animate-spin" />
                                                  : <DownloadIcon className="w-3 h-3" />}
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={data.page} pageSize={data.page_size}
                              total={data.total} onChange={setPage} />
            </>
          )}
    </div>
  );
}

// ─── Release notes panel ────────────────────────────────────────────────────
function ReleasesPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data } = await partnerApi.get(
        "/partner/portal/trust-center/release-notes",
        { params: { search: q || undefined, page, page_size: 20 } },
      );
      setData(data);
    } catch { setData({ items: [] }); }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <LoadingRow />;
  if (!data.items?.length)
    return <EmptyState label="No release notes yet." />;
  return (
    <div data-testid="tc-panel-release-notes">
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
                placeholder="Search releases…" className="hw-pp__input"
                data-testid="tc-release-search" style={{ flex: 1 }} />
      </div>
      <ol style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {data.items.map((r) => (
          <li key={r.id} className="hw-pp__release-card"
                data-testid={`tc-release-${r.version.replace(/\./g, "-")}`}>
            <header style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
              <div>
                <span style={{ fontFamily: "monospace", background: "rgba(94,234,212,0.15)",
                                 color: "#5EEAD4", padding: "2px 8px",
                                 borderRadius: 6, fontSize: 12 }}>
                  {r.version}
                </span>
                <h3 style={{ margin: "6px 0 0", fontSize: 18, color: "#F8FAFC" }}>{r.title}</h3>
              </div>
              <span style={{ fontSize: 12, color: "#94A3B8" }}>
                {fmtDate(r.released_at)}
              </span>
            </header>
            <ReleaseGroup label="✨ Features" items={r.features} />
            <ReleaseGroup label="🐞 Fixes" items={r.fixes} />
            <ReleaseGroup label="🔒 Security" items={r.security_notes} />
            <ReleaseGroup label="⚠️ Breaking changes" items={r.breaking_changes}
                             tone="warn" />
            {r.downloads?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div className="hw-pp__caption">Downloads</div>
                {r.downloads.map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noreferrer"
                      className="hw-pp__link" style={{ display: "block" }}
                      data-testid={`tc-release-dl-${r.id.slice(0, 6)}-${i}`}>
                    {d.label || d.url}
                  </a>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>
      <Pagination page={data.page} pageSize={data.page_size}
                      total={data.total} onChange={setPage} />
    </div>
  );
}

function ReleaseGroup({ label, items, tone }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div className="hw-pp__caption"
            style={{ color: tone === "warn" ? "#F87171" : "#94A3B8" }}>
        {label}
      </div>
      <ul style={{ margin: "4px 0 0", paddingLeft: 20, color: "#CBD5E1" }}>
        {items.map((it, i) => (<li key={i}>{it}</li>))}
      </ul>
    </div>
  );
}

// ─── Reusable atoms ─────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 20 }}>
      <h3 style={{ color: "#F8FAFC", fontSize: 14, textTransform: "uppercase",
                     letterSpacing: 1, marginBottom: 8 }}>{title}</h3>
      {children}
    </section>
  );
}
function StatCard({ label, value }) {
  return (
    <div className="hw-pp__stat-card">
      <div className="hw-pp__caption">{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: "#F8FAFC" }}>{value}</div>
    </div>
  );
}
function LoadingRow() {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <Loader2 className="w-6 h-6 animate-spin"
                style={{ margin: "0 auto", color: "#5EEAD4" }} />
    </div>
  );
}
function EmptyState({ label }) {
  return (
    <div style={{ padding: 32, textAlign: "center", color: "#94A3B8" }}
          data-testid="tc-empty-state">
      <AlertCircle className="w-6 h-6"
                     style={{ margin: "0 auto 6px", color: "#64748B" }} />
      <div>{label}</div>
    </div>
  );
}
function Pagination({ page, pageSize, total, onChange }) {
  const pages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center",
                    marginTop: 12, alignItems: "center" }}>
      <button disabled={page <= 1} onClick={() => onChange(page - 1)}
        className="hw-pp__btn hw-pp__btn--sm"
        data-testid="tc-pagination-prev">Prev</button>
      <span style={{ color: "#94A3B8", fontSize: 12 }}
             data-testid="tc-pagination-info">
        Page {page} of {pages} · {total} items
      </span>
      <button disabled={page >= pages} onClick={() => onChange(page + 1)}
        className="hw-pp__btn hw-pp__btn--sm"
        data-testid="tc-pagination-next">Next</button>
    </div>
  );
}
